import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Send, Copy, Check, Upload, Loader2, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import imageCompression from "browser-image-compression";

// Telegram de administración para recibir comprobantes
const ADMIN_TELEGRAM = "nusr11";

// Format number as Colombian Pesos
const formatCOP = (amount) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Payment method icons
const PAYMENT_ICONS = {
  nequi: "https://customer-assets.emergentagent.com/job_ed2a5928-734c-4717-9f2e-99379f5866cf/artifacts/mtmpkmhz_image.png",
  bancolombia: "https://play-lh.googleusercontent.com/K3NRWJPD3slE8rYvbMSX_wpjnsfWG5VlDxiM13TNGkvFvHFNHfhmCPisfJi3-0iQDBI",
  daviplata: "https://sunegocio.daviplata.com/sites/default/files/styles/original/public/2023-11/af2be4165905879.Y3JvcCwxNDAwLDEwOTUsMCwxNTI.png?itok=Fn_Y_HIf",
  paypal: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/1200px-PayPal.svg.png",
  binance: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e8/Binance_Logo.svg/1200px-Binance_Logo.svg.png",
  zelle: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8e/Zelle_logo.svg/1200px-Zelle_logo.svg.png",
  cashapp: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Square_Cash_app_logo.svg/1200px-Square_Cash_app_logo.svg.png",
  pse: "https://www.pfranciscano.edu.co/wp-content/uploads/2021/07/pse.png",
  efecty: "https://www.efecty.com.co/storage/app/uploads/public/5f3/f74/f6b/5f3f74f6b5d85099050133.png",
  llave: "https://customer-assets.emergentagent.com/job_ed2a5928-734c-4717-9f2e-99379f5866cf/artifacts/hf7djcda_image.png",
  "bre-b": "https://customer-assets.emergentagent.com/job_ed2a5928-734c-4717-9f2e-99379f5866cf/artifacts/hf7djcda_image.png",
  breb: "https://customer-assets.emergentagent.com/job_ed2a5928-734c-4717-9f2e-99379f5866cf/artifacts/hf7djcda_image.png",
  default: "https://cdn-icons-png.flaticon.com/512/3135/3135706.png"
};

// Get icon URL for payment method
const getPaymentIcon = (method) => {
  if (method.icon && method.icon.startsWith('http')) return method.icon;
  const key = method.name?.toLowerCase().replace(/\s/g, '');
  return PAYMENT_ICONS[key] || PAYMENT_ICONS.default;
};

const TelegramVipModal = ({ creator, onClose }) => {
  const [step, setStep] = useState(1); // 1: info + payment, 2: form, 3: success
  const [membershipType, setMembershipType] = useState("monthly");
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [telegramUsername, setTelegramUsername] = useState("");
  const [paymentProof, setPaymentProof] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [trm, setTrm] = useState(4200);
  const [copied, setCopied] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [requestId, setRequestId] = useState(null); // id of the created vip_request
  const [botUsername, setBotUsername] = useState(null);

  // Fetch bot username once on mount (used for deep-link after submit)
  useEffect(() => {
    fetch("/api/telegram-bot/public-info")
      .then((r) => r.json())
      .then((d) => { if (d.configured && d.botUsername) setBotUsername(d.botUsername); })
      .catch(() => { /* silent */ });
  }, []);
  const [globalPaymentMethods, setGlobalPaymentMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });

  const vipConfig = creator.telegramVip || {};
  
  // Check if offer is active
  const isOfferActive = vipConfig.offerEnabled && vipConfig.offerEndDate && new Date(vipConfig.offerEndDate) > new Date();
  
  // Format price based on payment method currency
  const formatMethodPrice = (method) => {
    const currency = method?.currency || "COP";
    if (currency === "USD") {
      return `$${selectedPrice} USD`;
    }
    return formatCOP(selectedPrice * trm);
  };

  // Format regular price for method
  const formatRegularPrice = (method) => {
    const currency = method?.currency || "COP";
    if (currency === "USD") {
      return `$${regularPrice} USD`;
    }
    return formatCOP(regularPrice * trm);
  };
  
  // Use global payment methods
  const paymentMethods = globalPaymentMethods;
  const hasPaymentMethods = paymentMethods.length > 0;
  
  // Calculate prices (offer vs regular)
  const regularMonthlyPrice = vipConfig.monthlyPrice || 0;
  const regularLifetimePrice = vipConfig.lifetimePrice || 0;
  
  const selectedPrice = membershipType === "monthly" 
    ? (isOfferActive && vipConfig.offerMonthlyPrice ? vipConfig.offerMonthlyPrice : regularMonthlyPrice)
    : (isOfferActive && vipConfig.offerLifetimePrice ? vipConfig.offerLifetimePrice : regularLifetimePrice);

  const regularPrice = membershipType === "monthly" ? regularMonthlyPrice : regularLifetimePrice;
  const hasDiscount = isOfferActive && selectedPrice < regularPrice;
  const discountPercent = hasDiscount ? Math.round((1 - selectedPrice / regularPrice) * 100) : 0;
  
  const selectedPlan = membershipType === "monthly" ? "Mensual" : "Lifetime";

  // Fetch TRM and global payment methods on mount
  useEffect(() => {
    const fetchTRM = async () => {
      try {
        const response = await fetch(
          'https://www.datos.gov.co/resource/32sa-8pi3.json?$limit=1&$order=vigenciadesde%20DESC'
        );
        const data = await response.json();
        if (data && data[0] && data[0].valor) {
          setTrm(parseFloat(data[0].valor));
        }
      } catch (error) {
        console.log('Using default TRM');
      }
    };
    const fetchPaymentMethods = async () => {
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${apiUrl}/api/settings/payment-methods`);
        const data = await response.json();
        if (data.methods && data.methods.length > 0) {
          setGlobalPaymentMethods(data.methods);
        }
      } catch (error) {
        console.log('Could not fetch global payment methods');
      } finally {
        setLoadingMethods(false);
      }
    };
    fetchTRM();
    fetchPaymentMethods();
  }, []);

  // Countdown timer for offer
  useEffect(() => {
    if (!isOfferActive || !vipConfig.offerEndDate) return;
    const update = () => {
      const now = new Date();
      const end = new Date(vipConfig.offerEndDate);
      const diff = Math.max(0, end - now);
      setCountdown({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        mins: Math.floor((diff % 3600000) / 60000),
        secs: Math.floor((diff % 60000) / 1000)
      });
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [isOfferActive, vipConfig.offerEndDate]);

  // Handle image upload with compression
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      toast.loading("Procesando imagen...", { id: 'upload' });
      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1200,
        useWebWorker: true
      };
      const compressedFile = await imageCompression(file, options);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPaymentProof(reader.result);
        toast.success("Imagen cargada", { id: 'upload' });
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      toast.error("Error al procesar la imagen", { id: 'upload' });
    }
  };

  // Generate the Telegram message
  const generateMessage = () => {
    const cleanUsername = telegramUsername.replace("@", "");
    const paymentName = selectedPayment?.name || "No especificado";
    return `🎫 SOLICITUD DE ACCESO VIP

Hola! Quiero acceso al Telegram VIP de *${creator.name}*

📱 Mi Telegram: @${cleanUsername}
📦 Plan: ${selectedPlan}
💰 Monto: $${selectedPrice} USD (${formatCOP(selectedPrice * trm)})
💳 Método de pago: ${paymentName}

⬇️ ADJUNTO MI COMPROBANTE DE PAGO ABAJO ⬇️`;
  };

  // Submit to backend AND open Telegram
  const handleSubmit = async () => {
    if (!telegramUsername.trim()) {
      toast.error("Ingresa tu usuario de Telegram");
      return;
    }
    if (!paymentProof) {
      toast.error("Sube el comprobante de pago");
      return;
    }

    setIsSubmitting(true);
    
    // Open Telegram first
    const message = encodeURIComponent(generateMessage());
    const telegramUrl = `https://t.me/${ADMIN_TELEGRAM}?text=${message}`;
    window.open(telegramUrl, '_blank');
    
    try {
      await fetch("/api/telegram-vip/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          creatorUsername: creator.username,
          creatorName: creator.name,
          telegramUsername: telegramUsername.replace("@", ""),
          membershipType,
          paymentProof,
          priceUSD: selectedPrice,
          priceCOP: Math.round(selectedPrice * trm),
          paymentMethod: selectedPayment?.name || "legacy"
        })
      });
      setStep(3);
      toast.success("¡Solicitud enviada!");
    } catch (error) {
      setStep(3);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Copy payment details
  const copyDetails = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  // Select payment method and show details
  const handleSelectPayment = (method) => {
    setSelectedPayment(method);
    setShowPaymentDetails(true);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-white/10">
        {/* Header */}
        <div className="sticky top-0 bg-[#111] p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-white font-semibold">Telegram VIP</h2>
              <p className="text-white/50 text-sm">{creator.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {step === 1 && !showPaymentDetails && (
            <div className="space-y-4">
              <p className="text-white/70 text-sm">
                Accede al contenido exclusivo de {creator.name} en Telegram
              </p>

              {/* Offer Banner with Countdown */}
              {hasDiscount && (
                <div className="relative overflow-hidden rounded-xl border border-yellow-500/30 bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-red-500/10 p-4" data-testid="offer-banner">
                  <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.05)_50%,transparent_100%)] animate-[shimmer_2s_infinite]" />
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl animate-bounce">🔥</span>
                      <p className="text-yellow-400 font-bold text-sm flex-1">
                        {vipConfig.offerLabel || `¡OFERTA EXCLUSIVA -${discountPercent}%!`}
                      </p>
                      <div className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                        -{discountPercent}%
                      </div>
                    </div>
                    {/* Countdown Timer */}
                    <div className="flex items-center justify-center gap-2">
                      <div className="bg-black/40 rounded-lg px-2.5 py-1.5 text-center min-w-[48px]">
                        <p className="text-white font-bold text-lg leading-none">{countdown.days}</p>
                        <p className="text-white/40 text-[10px] mt-0.5">días</p>
                      </div>
                      <span className="text-white/30 font-bold">:</span>
                      <div className="bg-black/40 rounded-lg px-2.5 py-1.5 text-center min-w-[48px]">
                        <p className="text-white font-bold text-lg leading-none">{String(countdown.hours).padStart(2, '0')}</p>
                        <p className="text-white/40 text-[10px] mt-0.5">hrs</p>
                      </div>
                      <span className="text-white/30 font-bold">:</span>
                      <div className="bg-black/40 rounded-lg px-2.5 py-1.5 text-center min-w-[48px]">
                        <p className="text-white font-bold text-lg leading-none">{String(countdown.mins).padStart(2, '0')}</p>
                        <p className="text-white/40 text-[10px] mt-0.5">min</p>
                      </div>
                      <span className="text-white/30 font-bold">:</span>
                      <div className="bg-black/40 rounded-lg px-2.5 py-1.5 text-center min-w-[48px]">
                        <p className="text-red-400 font-bold text-lg leading-none animate-pulse">{String(countdown.secs).padStart(2, '0')}</p>
                        <p className="text-white/40 text-[10px] mt-0.5">seg</p>
                      </div>
                    </div>
                    <p className="text-white/40 text-xs text-center mt-2">¡No te quedes sin tu acceso!</p>
                  </div>
                </div>
              )}

              {/* Membership Options */}
              <div className="space-y-2">
                <button
                  onClick={() => setMembershipType("monthly")}
                  className={`w-full p-4 rounded-xl border transition-all flex items-center justify-between ${
                    membershipType === "monthly"
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-white/10 bg-white/5 hover:bg-white/10"
                  }`}
                  data-testid="plan-monthly"
                >
                  <div className="text-left">
                    <p className="text-white font-medium">Mensual</p>
                    <p className="text-white/50 text-sm">Acceso por 30 días</p>
                  </div>
                  <div className="text-right">
                    {isOfferActive && vipConfig.offerMonthlyPrice > 0 && vipConfig.offerMonthlyPrice < regularMonthlyPrice ? (
                      <>
                        <p className="text-sm text-white/40 line-through">${regularMonthlyPrice} USD</p>
                        <p className="text-2xl font-bold text-green-400">${vipConfig.offerMonthlyPrice} USD</p>
                        <p className="text-sm text-white/60">{formatCOP((vipConfig.offerMonthlyPrice) * trm)}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-2xl font-bold text-white">${regularMonthlyPrice} USD</p>
                        <p className="text-sm text-white/60">{formatCOP(regularMonthlyPrice * trm)}</p>
                      </>
                    )}
                  </div>
                </button>

                <button
                  onClick={() => setMembershipType("lifetime")}
                  className={`w-full p-4 rounded-xl border transition-all flex items-center justify-between ${
                    membershipType === "lifetime"
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-white/10 bg-white/5 hover:bg-white/10"
                  }`}
                  data-testid="plan-lifetime"
                >
                  <div className="text-left">
                    <p className="text-white font-medium">Lifetime</p>
                    <p className="text-white/50 text-sm">Acceso permanente</p>
                  </div>
                  <div className="text-right">
                    {isOfferActive && vipConfig.offerLifetimePrice > 0 && vipConfig.offerLifetimePrice < regularLifetimePrice ? (
                      <>
                        <p className="text-sm text-white/40 line-through">${regularLifetimePrice} USD</p>
                        <p className="text-2xl font-bold text-green-400">${vipConfig.offerLifetimePrice} USD</p>
                        <p className="text-sm text-white/60">{formatCOP((vipConfig.offerLifetimePrice) * trm)}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-2xl font-bold text-white">${regularLifetimePrice} USD</p>
                        <p className="text-sm text-white/60">{formatCOP(regularLifetimePrice * trm)}</p>
                      </>
                    )}
                  </div>
                </button>
              </div>

              {/* Payment Methods */}
              <div className="pt-2">
                <p className="text-white/50 text-xs mb-3">Selecciona método de pago</p>
                
                {loadingMethods ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="w-5 h-5 border-2 border-white/20 border-t-blue-500 rounded-full animate-spin" />
                  </div>
                ) : hasPaymentMethods ? (
                  <div className="space-y-2">
                    {paymentMethods.map((method, index) => (
                      <button
                        key={method.id || index}
                        onClick={() => handleSelectPayment(method)}
                        className={`w-full p-3 rounded-xl border transition-all flex items-center gap-3 ${
                          selectedPayment?.name === method.name
                            ? "border-blue-500 bg-blue-500/10"
                            : "border-white/10 bg-white/5 hover:bg-white/10"
                        }`}
                      >
                        <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center p-1 flex-shrink-0">
                          <img 
                            src={getPaymentIcon(method)} 
                            alt={method.name}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.target.src = PAYMENT_ICONS.default }}
                          />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-white font-medium">{method.name}</p>
                          <p className="text-white/50 text-sm">
                            Precio: {formatMethodPrice(method)}
                          </p>
                        </div>
                        {selectedPayment?.name === method.name && (
                          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  // Legacy fallback
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <p className="text-white/50 text-xs mb-2">Información de pago</p>
                    <p className="text-white text-sm whitespace-pre-line">
                      {vipConfig.paymentInfo || "Contactar para información de pago"}
                    </p>
                    <button
                      onClick={() => copyDetails(vipConfig.paymentInfo || "")}
                      className="mt-3 text-blue-400 text-xs flex items-center gap-1 hover:text-blue-300"
                    >
                      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copied ? "Copiado" : "Copiar información"}
                    </button>
                  </div>
                )}
              </div>

              <Button
                onClick={() => {
                  if (hasPaymentMethods && !selectedPayment) {
                    toast.error("Selecciona un método de pago");
                    return;
                  }
                  setStep(2);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Continuar
              </Button>
            </div>
          )}

          {/* Payment Details Modal */}
          {step === 1 && showPaymentDetails && selectedPayment && (
            <div className="space-y-4">
              <button 
                onClick={() => setShowPaymentDetails(false)}
                className="text-white/60 text-sm flex items-center gap-1 hover:text-white"
              >
                ← Volver a métodos de pago
              </button>

              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center p-2">
                  <img 
                    src={getPaymentIcon(selectedPayment)} 
                    alt={selectedPayment.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <p className="text-white font-semibold">{selectedPayment.name}</p>
                  <p className="text-white/50 text-sm">
                    {selectedPlan} - {hasDiscount && <span className="line-through text-white/30 mr-1">{formatRegularPrice(selectedPayment)}</span>}{formatMethodPrice(selectedPayment)}
                  </p>
                </div>
              </div>

              {selectedPayment.paypalLink ? (
                <div className="space-y-3">
                  <p className="text-white/70 text-sm">Haz clic para realizar el pago:</p>
                  <Button
                    onClick={() => window.open(selectedPayment.paypalLink, '_blank')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ir a pagar
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-white/70 text-sm">Datos para realizar el pago:</p>
                  <div className="bg-white/10 rounded-xl p-4 border border-white/20">
                    <p className="text-white font-mono text-lg break-all">{selectedPayment.details}</p>
                  </div>
                  <Button
                    onClick={() => copyDetails(selectedPayment.details)}
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/10 gap-2"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? "¡Copiado!" : "Copiar datos"}
                  </Button>
                </div>
              )}

              <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
                <p className="text-blue-400 text-sm">
                  <strong>Importante:</strong> Realiza el pago exacto de {formatMethodPrice(selectedPayment)}
                </p>
              </div>

              <Button
                onClick={() => {
                  setShowPaymentDetails(false);
                  setStep(2);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Ya realicé el pago
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-white/70 text-sm">
                Una vez realices el pago, completa el formulario
              </p>

              {/* Telegram Username */}
              <div>
                <Label className="text-white/50 text-sm mb-2 block">Tu usuario de Telegram</Label>
                <Input
                  value={telegramUsername}
                  onChange={(e) => setTelegramUsername(e.target.value)}
                  placeholder="@tuusuario"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="telegram-username-input"
                />
                {/* Prominent warning banner */}
                <div className="mt-2 p-3 rounded-lg bg-red-500/10 border border-red-400/40 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
                  <div className="text-red-100 text-xs leading-relaxed">
                    <p className="font-black uppercase tracking-wide mb-1 text-red-200">⚠️ IMPORTANTE — LEÉ ANTES DE PAGAR</p>
                    <p>
                      Tu usuario de Telegram debe estar <strong className="text-white">exactamente escrito</strong> (respeta mayúsculas, guiones bajos y números).
                      Si te equivocás una sola letra <strong className="text-white">no vamos a poder agregarte al canal VIP</strong> y perderás tu pago.
                    </p>
                    <p className="mt-1.5 text-red-200/90">
                      Verificalo en Telegram: <strong>Ajustes → Nombre de usuario</strong>. Copiá y pegá aquí.
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Proof */}
              <div>
                <Label className="text-white/50 text-sm mb-2 block">Comprobante de pago</Label>
                {paymentProof ? (
                  <div className="relative rounded-xl overflow-hidden bg-white/5">
                    <img src={paymentProof} alt="Comprobante" className="w-full h-auto max-h-48 object-contain" />
                    <button
                      onClick={() => setPaymentProof(null)}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 flex items-center justify-center"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <label className="block w-full py-8 rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 transition-colors cursor-pointer">
                    <div className="flex flex-col items-center justify-center gap-2 text-white/60">
                      <Upload className="w-8 h-8" />
                      <span className="text-sm">Subir comprobante</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Summary */}
              <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Plan:</span>
                  <span className="text-white font-medium">{selectedPlan}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-white/60">Precio:</span>
                  <span className="text-white font-medium">${selectedPrice} USD ({formatCOP(selectedPrice * trm)})</span>
                </div>
                {selectedPayment && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-white/60">Método:</span>
                    <span className="text-white font-medium">{selectedPayment.name}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                  disabled={isSubmitting}
                >
                  Atrás
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white gap-2"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar solicitud
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">¡Solicitud enviada!</h3>

              {/* Bot activation CTA — only if bot username is available */}
              {botUsername && requestId && (
                <div className="bg-gradient-to-br from-pink-500/15 to-rose-500/10 border border-pink-400/40 rounded-2xl p-4 mb-4 text-left">
                  <p className="text-pink-100 font-bold text-sm mb-2 flex items-center gap-1.5">
                    <Send className="w-4 h-4 text-pink-300" />
                    Paso importante — activá el bot
                  </p>
                  <p className="text-white/80 text-xs leading-relaxed mb-3">
                    Tocá el botón abajo, abrí <strong className="text-pink-200">@{botUsername}</strong> y apretá <strong className="text-white">INICIAR</strong>.
                    Cuando aprobemos tu pago, el <strong>link del canal VIP te llega automáticamente a ese chat</strong>.
                  </p>
                  <Button
                    onClick={() => {
                      window.open(`https://t.me/${botUsername}?start=vip_${requestId}`, '_blank');
                    }}
                    data-testid="activate-bot-btn"
                    className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-bold gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Activar bot y recibir mi link
                  </Button>
                </div>
              )}

              <p className="text-white/60 text-sm mb-4">
                También abrimos el chat con el admin para que envíes el comprobante. Si no se abrió, tocá acá:
              </p>
              <Button
                onClick={() => {
                  const message = encodeURIComponent(generateMessage());
                  window.open(`https://t.me/${ADMIN_TELEGRAM}?text=${message}`, '_blank');
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 mb-4"
              >
                <Send className="w-4 h-4" />
                Abrir chat con admin
              </Button>
              <p className="text-white/50 text-xs mb-6">
                Te contactaremos a @{telegramUsername.replace("@", "")} una vez verifiquemos el pago.
              </p>
              <Button onClick={onClose} className="bg-white/10 hover:bg-white/20 text-white">
                Cerrar
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TelegramVipModal;
